// =====================================================================
// src/modulos/checkin.js — Check-in: quem chegou, quem saiu
//
// O painel de um dia (hoje por padrão): as reservas que chegam, as
// hospedadas e as que saem, cada uma com a sua gente. Check-in é por
// pessoa (botão em cada linha); check-out é da reserva (um botão, todos
// saem). A regra mora em 0017: documento obrigatório, menor entra com o
// titular, janela de datas, quem alcança o quê. Quem só VÊ enxerga sem
// botões. Quem não tem `hospedes` vê o nome e "com/sem documento", não o
// número.
// =====================================================================
import { hojeDe } from '../lib/contexto.js';
import { el, icone, aviso, confirmar, enquantoAtualiza, barraDeAbas, abrirModal, fecharModal } from '../ui.js';
import { DIAS, diaDaSemana } from '../lib/escalas.js';
import { formatarDocumento } from '../lib/hospedes.js';
import {
  dataISO, somarDias, dataCurta, noites, FASES_CHECKIN, ABAS_CHECKIN, agruparCheckin, filtrarCheckin, resumoCheckin,
  acaoDoHospede, podeCheckout, podeDesfazerCheckout, horaCurta,
} from '../lib/checkin.js';
import { rotuloFaixa, MOTIVOS_VIAGEM, MEIOS_TRANSPORTE, SEXOS, RACAS, DEFICIENCIAS, TIPOS_DEFICIENCIA } from '../lib/precheckin.js';
import { seletorPais, blocoResidencia } from './endereco.js';
import { dataBR } from '../lib/reservas.js';   // a data por extenso do MdM
import { avisoDoDisparo } from '../lib/fnrh.js';
import { CAMPOS_FNRH, argumentosDoHospede, fichaDoHospede, separarNome, nomeCompleto, validarHospede, TIPOS_DOCUMENTO, errosDaFicha, fichaParaSalvar, MAX_GENERO_DESCRICAO, textoAntigo } from '../lib/hospedes.js';
import {
  buscaPronta, situacaoDaLinha, faltaParaAChave, podeEntregarChave,
  origemDaFicha, fnrhPreenchidos, totalFnrh, reencontrar, separarFicha, iniciaAtendimento,
} from '../lib/balcao.js';
import { vagaDoPrimeiroCheckin, seloDaVaga, normalizarPlaca } from '../lib/garagem.js';
import { modalReservaDeVaga } from './reserva_de_vaga.js';

export function montar(cont, { auth, contexto, agora = () => Date.now() }) {
  const nivelDe = ch => (contexto.modulos.find(m => m.chave === ch) || {}).nivel;
  const podeEditar = nivelDe('checkin') === 'editar';
  const temHospedes = !!nivelDe('hospedes');
  const sistema = !!(contexto.usuario && contexto.usuario.sistema);
  // 0097 (6A): a vaga da garagem no balcão — para quem edita a Garagem. O
  // Administrador do Sistema não: as funções da garagem são do hotel do
  // login (app.cliente), e ele não tem hotel.
  const editaGaragem = !sistema && nivelDe('garagem') === 'editar';
  let vagas = [];   // as vagas confirmadas das reservas do dia (vagas_das_reservas)
  const vagasDe = id => vagas.filter(v => v.reserva_id === id);
  let vivo = true;
  let linhas = [], clientes = [];
  // 0064: o hotel coleta raça/cor? (módulo FNRH contratado) — o banco recusa
  // sem o contrato; a tela só esconde o campo que não pode ser gravado
  let coletaRaca = false;
  // 0071: o envio à API da FNRH está ligado neste hotel? (módulo FNRH
  // contratado E chave cadastrada). Desligado, a tela não chama a função.
  let fnrhLigado = false;
  let clienteSel = sistema ? null : contexto.cliente.id;
  const hoje = () => hojeDe(contexto, agora());   // o dia do hotel (0020), não o do aparelho
  let dia = hoje();
  let filtro = { aba: 'chegadas', texto: '' };
  const pc = () => (sistema ? { p_cliente: clienteSel } : {});
  // A BUSCA DO BALCÃO (0040). Estado próprio, porque ela não é o filtro da
  // lista do dia: o `filtro.texto` peneira o que já está na tela, e esta
  // vai ao banco atrás do que NÃO está — a reserva de amanhã, a do hóspede
  // que chegou um dia atrasado. Misturar os dois faria a barra parecer
  // uma só e se comportar como duas.
  let balcao = { texto: '', achados: [], abertaId: null, ficha: null, buscando: false, editando: null, vagas: [] };

  async function carregar() {
    if (sistema) { clientes = await auth.ler('clientes', 'select=id,slug,nome&order=nome'); if (!clienteSel && clientes.length) clienteSel = clientes[0].id; }
    if (!clienteSel) { linhas = []; return; }
    [linhas, coletaRaca, fnrhLigado, vagas] = await Promise.all([
      auth.rpc('checkin_do_dia', { p_data: dia, ...pc() }),
      auth.rpc('coleta_raca', { ...pc() }).then(Boolean, () => false),
      auth.rpc('fnrh_envio_ligado', { ...pc() }).then(Boolean, () => false),
      editaGaragem ? auth.rpc('vagas_das_reservas', { p_de: dia, p_ate: dia }).then(x => x || []) : [],
    ]);
  }
  // enquantoAtualiza: o <html> conta os ciclos, e a prova espera o contador
  // crescer em vez de adivinhar que a tela ja se refez (ui.js).
  function recarregar() {
    return enquantoAtualiza(async () => {
      try { await carregar(); } catch (e) { aviso(e.message, 'erro'); return; }
      desenhar();
    });
  }

  // Só a lista do dia, sem redesenhar a tela: a recepcionista pode estar
  // digitando na busca do balcão, e redesenhar tudo tiraria o foco dela.
  function atualizarLista() {
    return enquantoAtualiza(async () => {
      try { await carregar(); } catch (e) { aviso(e.message, 'erro'); return; }
      if (vivo) desenharLista();
    });
  }

  // `_ckCartao` do MdM (index.html, 20475), letra por letra.
  function cartaoContador(rotulo, valor, cor) {
    return el('div', { style: { flex: '1', minWidth: '110px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px 12px' }, dataset: { contador: rotulo } },
      el('div', { style: { fontSize: '11px', color: '#6b7280', fontWeight: '600' } }, rotulo),
      el('div', { style: { fontSize: '22px', fontWeight: '700', color: cor, fontVariantNumeric: 'tabular-nums' } }, String(valor)));
  }

  function desenhar() {
    if (!vivo) return;
    cont.replaceChildren();
    cont.dataset.tela = 'checkin';
    const lista = agruparCheckin(linhas, dia);
    const r = resumoCheckin(lista);
    const h = hoje();
    cont.appendChild(el('div', { class: 'gov-cabeca' },
      el('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' } },
        sistema ? el('select', { class: 'input', id: 'ci-cliente', style: { width: 'auto' }, onchange: ev => { clienteSel = ev.target.value; recarregar(); } },
          clientes.map(c => el('option', { value: c.id, selected: c.id === clienteSel }, c.nome))) : null,
        el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'dia-anterior', 'aria-label': 'Dia anterior', onclick: () => { dia = somarDias(dia, -1); recarregar(); } }, '‹'),
        el('input', { type: 'date', class: 'input', id: 'dia', value: dia, style: { width: 'auto', fontSize: '14px', padding: '7px 10px' }, onchange: ev => { if (ev.target.value) { dia = ev.target.value; recarregar(); } } }),
        el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'dia-seguinte', 'aria-label': 'Dia seguinte', onclick: () => { dia = somarDias(dia, 1); recarregar(); } }, '›'),
        dia !== h ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'dia-hoje', onclick: () => { dia = h; recarregar(); } }, 'Hoje') : null,
        el('span', { style: { fontSize: '13px', color: '#6b7280' }, id: 'dia-nome' }, `${DIAS[diaDaSemana(dia)]}${dia === h ? ' · hoje' : ''}`)),
      null));

    // A LINHA DE CONTEXTO E OS CINCO CARTÕES DO MONTEIRO DE MORAES
    // `renderCheckin` (MdM 20373) abre com um subtítulo de 13px cinza
    // dizendo de que dia são as chegadas e a partir de que hora entra, e
    // logo abaixo cinco cartões `_ckCartao` (MdM 20475): rótulo 11px cinza
    // 600, número 22px/700 na cor do assunto, borda #e5e7eb, raio 10.
    // Eu tinha quatro pílulas `.badge` espremidas no canto — a informação
    // era a mesma, mas ninguém lê um número de 12px no canto da tela.
    cont.appendChild(el('div', { style: { fontSize: '13px', color: '#6b7280', marginBottom: '14px' }, id: 'ci-contexto' },
      (dia === h ? 'Chegadas de hoje' : 'Chegadas de ' + dataBR(dia))
      + (contexto.cliente && contexto.cliente.hora_checkin ? ` · entrada a partir das ${String(contexto.cliente.hora_checkin).slice(0, 5)}` : '')));
    cont.appendChild(el('div', { id: 'ci-resumo', style: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' } },
      cartaoContador('Chegadas', r.chegam, '#1B3A7A'),
      cartaoContador('Recebidos', r.chegaram, '#16a34a'),
      cartaoContador('Faltam', r.chegam - r.chegaram, (r.chegam - r.chegaram) ? '#b45309' : '#6b7280'),
      cartaoContador('Saídas', `${r.sairam}/${r.saem}`, r.saem - r.sairam ? '#b45309' : '#6b7280'),
      // O "Sem telefone" dele existe para a recepção saber a quem não dá para
      // ligar. No hotel o que trava a entrega da chave é outro: sem documento
      // não há FNRH, e sem FNRH o hóspede não entra. Mesma ideia, outro campo.
      cartaoContador('Sem documento', r.pendentes, r.pendentes ? '#dc2626' : '#6b7280')));
    cont.appendChild(secaoBalcao());
    cont.appendChild(el('div', { class: 'usu-filtros' },
      barraDeAbas('ci-abas', ABAS_CHECKIN.map(([ch, rot]) => ({ chave: ch, rotulo: rot })), filtro.texto ? null : filtro.aba,
        ch => { filtro.aba = ch; filtro.texto = ''; desenhar(); }),
      el('input', { type: 'search', class: 'input', id: 'ci-busca', placeholder: 'Quarto, nome ou documento', value: filtro.texto, oninput: ev => { filtro.texto = ev.target.value; desenharLista(); } })));
    cont.appendChild(el('div', { id: 'ci-lista' }));
    desenharLista();
  }

  // =====================================================================
  // A BUSCA DO BALCÃO E O QUADRO QUE ELA ABRE (0040 + 0038)
  //
  // Pedido do Hugo, 19/09/2026:
  //
  //   "colocar opcao dos funcionarios do check-in terem uma barra para
  //    fazer busca do hospede previamente cadastrado digitando seu nome,
  //    mostrando as informacoes da reserva em um quadro aberto, com os
  //    campos de pre check in (caso tenham feito), ou preenchimento pela
  //    recepcao, caso nao tenham feito."
  //
  // O DESENHO NÃO É MEU. O `renderCheckin` do Monteiro de Moraes (MdM
  // 20373) não tem busca nenhuma — ele termina dizendo, em cinza, que "a
  // ficha do hóspede entra numa próxima etapa". Mas o MdM RESOLVEU este
  // desenho em outro lugar: `renderHospedes` (MdM 15565) tem exatamente
  // isto — coluna de busca de 300px com a lupa DENTRO do campo, os
  // resultados num card rolável de 260px, e o detalhe em largura total
  // embaixo, com a linha escolhida em #eff6ff e o nome em 22px/700
  // #1B3A7A ao lado (MdM 14639). Foi de lá que a marcação veio, medida
  // por medida.
  //
  // A divergência (regra 4.1 do CLAUDE.md, e ela vira o comentário): lá o
  // ponto verde/vermelho ao lado do nome quer dizer "cadastro completo".
  // Aqui quer dizer uma coisa mais dura — **dá para entregar a chave?** —,
  // porque no hotel sem documento não há FNRH e sem FNRH o hóspede não
  // entra. Mesmo sinal, outra consequência.
  // =====================================================================

  function secaoBalcao() {
    const caixa = el('div', { class: 'card', id: 'balcao', style: { padding: '14px 16px', marginBottom: '14px' } },
      el('div', { style: { fontSize: '13px', color: '#6b7280', marginBottom: '10px' }, id: 'balcao-dica' },
        'O hóspede não está na lista do dia? Procure pelo nome — a busca alcança as reservas que ainda vão começar.'),
      el('div', { style: { display: 'flex', gap: '16px', alignItems: 'start', flexWrap: 'wrap' } },
        el('div', { style: { width: '300px', flexShrink: '0' } },
          el('div', { style: { position: 'relative', marginBottom: '10px' } },
            el('input', {
              class: 'input', id: 'balcao-busca', type: 'search', autocomplete: 'off',
              placeholder: 'Buscar por nome ou documento...', value: balcao.texto,
              style: { paddingLeft: '36px' },
              oninput: ev => procurar(ev.target.value),
            }),
            el('span', { style: { position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' } },
              icone('ic-search'))),
          el('div', { class: 'card', id: 'balcao-resultados', style: { padding: '8px', maxHeight: '260px', overflowY: 'auto' } })),
        el('div', { id: 'balcao-cabeca', style: { flex: '1', minWidth: '260px', minHeight: '80px', display: 'flex', alignItems: 'center' } })),
      el('div', { id: 'balcao-quadro' }));
    // desenhado depois de existir no DOM, como o MdM faz com `filtrarHospedes`
    queueMicrotask(() => { desenharResultados(); desenharQuadro(); });
    return caixa;
  }

  // A busca vai ao banco. Não há atraso artificial: o `buscaPronta` já
  // segura as duas primeiras letras, que é onde estaria o desperdício, e
  // um `setTimeout` de 300ms faria a lista chegar DEPOIS do hóspede
  // terminar de falar o nome.
  async function procurar(texto) {
    balcao.texto = texto;
    if (!buscaPronta(texto)) { balcao.achados = []; desenharResultados(); return; }
    balcao.buscando = true; desenharResultados();
    let r;
    try { r = await auth.rpc('buscar_no_balcao', { p_texto: texto, ...pc() }); }
    catch (e) { balcao.buscando = false; balcao.achados = []; desenharResultados(); aviso(e.message, 'erro'); return; }
    // A recepcionista continuou digitando enquanto a resposta vinha: esta
    // é de uma busca velha e escrevê-la na tela faria a lista piscar para
    // trás. Descartada em silêncio.
    if (balcao.texto !== texto) return;
    balcao.buscando = false; balcao.achados = r || [];
    desenharResultados();
  }

  function desenharResultados() {
    const alvo = cont.querySelector('#balcao-resultados'); if (!alvo) return;
    alvo.replaceChildren();
    if (balcao.buscando) { alvo.appendChild(el('p', { style: vazioBalcao, id: 'balcao-buscando' }, 'Procurando…')); return; }
    if (!buscaPronta(balcao.texto)) {
      alvo.appendChild(el('p', { style: vazioBalcao, id: 'balcao-digite' }, 'Digite pelo menos 3 letras…'));
      return;
    }
    if (!balcao.achados.length) {
      // Diz onde NÃO procurou. "Nada encontrado" faria a recepcionista
      // digitar o nome de novo, mais devagar, achando que errou.
      alvo.appendChild(el('p', { style: vazioBalcao, id: 'balcao-nada' }, 'Ninguém com esse nome em reserva aberta. Quem já foi embora fica na tela de Hóspedes.'));
      return;
    }
    for (const l of balcao.achados) alvo.appendChild(linhaAchada(l));
  }

  const vazioBalcao = { color: '#9ca3af', fontSize: '13px', padding: '12px', textAlign: 'center', margin: '0' };

  // A linha da lista de resultados. `.hospede-row` do MdM (15565), com o
  // realce #eff6ff na escolhida — dali mesmo (MdM 14711).
  function linhaAchada(l) {
    const s = situacaoDaLinha(l, hoje());
    const aberta = balcao.abertaId === l.reserva_id;
    return el('div', {
      class: 'linha-clicavel', dataset: { achado: l.reserva_id, situacao: s.chave },
      style: { padding: '8px 10px', borderRadius: '8px', marginBottom: '4px', background: aberta ? '#eff6ff' : '' },
      onclick: () => abrirFicha(l.reserva_id),
    },
      el('div', { style: { fontWeight: '600', fontSize: '13px' } }, l.nome),
      el('div', { style: { fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' } },
        el('span', null, `Quarto ${l.quarto}`),
        el('span', { class: 'badge ' + s.classe }, s.rotulo),
        el('span', null, `${dataCurta(l.check_in, hoje())} → ${dataCurta(l.check_out, hoje())}`)));
  }

  async function abrirFicha(reservaId, { jaAtendida = false } = {}) {
    balcao.abertaId = reservaId; balcao.editando = null;
    desenharResultados();
    let linhas, vagasDaFicha;
    try {
      [linhas, vagasDaFicha] = await Promise.all([
        auth.rpc('ficha_da_reserva', { p_reserva: reservaId }),
        editaGaragem ? auth.rpc('vagas_das_reservas', { p_reserva: reservaId }).then(x => x || []) : [],   // 0097
      ]);
    }
    catch (e) { balcao.abertaId = null; balcao.ficha = null; desenharResultados(); desenharQuadro(); aviso(e.message, 'erro'); return; }
    if (balcao.abertaId !== reservaId) return;   // clicou em outra enquanto vinha
    balcao.ficha = separarFicha(linhas);
    balcao.vagas = vagasDaFicha;
    desenharQuadro();
    // o relógio do balcão (0060): marca o início na primeira abertura da
    // chegada do dia. Não segura a tela — e falhar aqui não impede o
    // check-in: o indicador perde um ponto, o atendimento não para.
    // quem só VÊ o check-in (a supervisão conferindo) não atende: não marca
    // 0061: depois de gravar, a lista do dia recarrega — é ela que libera
    // os botões Check-in da reserva atendida
    if (!jaAtendida && podeEditar && iniciaAtendimento(balcao.ficha, hoje()))
      auth.rpc('iniciar_atendimento', { p_reserva: reservaId, ...pc() })
        .then(() => atualizarLista(), e => console.warn('iniciar_atendimento:', e.message));
  }

  // 0061 (Hugo, 25/09 — opção A): o Atender da lista do dia. Grava o início
  // ANTES de mostrar os Check-in, e abre a ficha no quadro do balcão, onde a
  // recepcionista vê o que falta (documento, FNRH) antes da chave. Aqui a
  // falha avisa: sem o início gravado os botões não apareceriam, e a
  // recepção precisa saber por quê.
  async function atender(r) {
    try { await auth.rpc('iniciar_atendimento', { p_reserva: r.id, ...pc() }); }
    catch (e) { aviso(e.message, 'erro'); return; }
    await recarregar();
    await abrirFicha(r.id, { jaAtendida: true });   // o início já foi gravado aqui
    const b = cont.querySelector('#balcao');
    if (b && b.scrollIntoView) b.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function fecharFicha() {
    balcao.abertaId = null; balcao.ficha = null; balcao.editando = null;
    desenharResultados(); desenharQuadro();
  }

  function desenharQuadro() {
    const cabeca = cont.querySelector('#balcao-cabeca');
    const quadro = cont.querySelector('#balcao-quadro');
    if (!cabeca || !quadro) return;
    cabeca.replaceChildren(); quadro.replaceChildren();
    if (!balcao.ficha || !balcao.ficha.reserva) {
      cabeca.appendChild(el('p', { style: { color: '#9ca3af', fontSize: '14px', margin: '0' }, id: 'balcao-sem-selecao' },
        'Escolha um hóspede para abrir a reserva.'));
      return;
    }
    const { reserva, pessoas } = balcao.ficha;
    const chave = podeEntregarChave(pessoas);
    // O ponto verde/vermelho do MdM (14640): 13px, borda branca de 2px e
    // um halo da própria cor. Lá ele diz "cadastro completo"; aqui diz se
    // a chave sai.
    const cor = chave ? '#10b981' : '#ef4444';
    const dica = chave ? 'Documento e nascimento de todos — a chave pode sair' : 'Falta documento ou nascimento — a chave não sai';
    cabeca.appendChild(el('div', { style: { width: '100%' } },
      el('div', { style: { fontSize: '22px', fontWeight: '700', color: '#1B3A7A', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' } },
        el('span', null, 'Quarto ' + reserva.quarto),
        el('span', {
          id: 'balcao-farol', title: dica, dataset: { chave: chave ? 'sim' : 'nao' },
          style: { display: 'inline-block', width: '13px', height: '13px', borderRadius: '50%', background: cor, border: '2px solid white', boxShadow: `0 0 0 2px ${cor}44`, flexShrink: '0' },
        })),
      el('div', { class: 'usu-email', id: 'balcao-resumo' },
        `${dataCurta(reserva.check_in, hoje())} → ${dataCurta(reserva.check_out, hoje())} · ${noites(reserva.check_in, reserva.check_out)} noite(s)`
        + ` · ${pessoas.length} de ${reserva.capacidade} pessoa(s)`
        + (reserva.numero ? ` · nº ${reserva.numero}` : '')),
      el('div', { class: 'usu-email', id: 'balcao-farol-texto', style: { color: cor, fontWeight: '600', marginTop: '4px' } }, dica),
      el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'balcao-fechar', style: { marginTop: '8px' }, onclick: fecharFicha }, 'Fechar')));

    // `data-reserva` no quadro não é decoração: sem ele, quem espera "o
    // quadro apareceu" vê o quadro ANTERIOR, que ainda está na tela
    // enquanto a nova ficha vem do banco — a mesma armadilha do toast
    // descrita no cabeçalho de `prova_checkin_offline.mjs`, e ela pegou
    // esta prova na primeira rodada.
    quadro.appendChild(el('div', { style: { maxWidth: '760px', margin: '14px auto 0' }, id: 'balcao-pessoas', dataset: { reserva: reserva.id } },
      editaGaragem ? cartaoGaragem(reserva, pessoas, balcao.vagas) : null,
      pessoas.map(p => blocoPessoa(reserva, p))));
  }

  // ── A GARAGEM NA FICHA (0097 — 6A) ─────────────────────────────────
  // O cartão Garagem: a vaga da reserva e o que falta para ela valer na
  // guarita. A placa é pedida no 1º check-in (1B); este cartão é para o
  // que sobra — chegou sem carro, o carro vem depois, ou a reserva tem mais
  // de uma vaga. Sem vaga, o atalho para reservar (2A). A marcação é a do
  // `.card` do bloco da pessoa, logo abaixo, com o selo em badge.
  function cartaoGaragem(reserva, pessoas, vs) {
    const dentro = pessoas.some(p => p.chegada_em && !p.saida_em);
    const recarregarFicha = () => { atualizarLista(); abrirFicha(reserva.id, { jaAtendida: true }); };
    const titulo = extra => el('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' } },
      icone('ic-garagem'), el('b', { style: { fontSize: '15px' } }, 'Garagem'), extra);
    if (!vs.length) {
      if (reserva.situacao !== 'confirmada') return null;
      const titular = pessoas.find(p => p.principal);
      return el('div', { class: 'card', id: 'balcao-garagem', dataset: { vagas: '0' }, style: { padding: '14px 16px', marginBottom: '12px' } },
        titulo(el('span', { class: 'usu-email' }, '— reserva sem vaga')),
        el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'balcao-reservar-vaga', onclick: () => modalReservaDeVaga({
          auth, reserva: { id: reserva.id, quarto: reserva.quarto, hospede: titular ? titular.nome : null, check_in: reserva.check_in, check_out: reserva.check_out },
          aoSalvar: recarregarFicha }) }, '+ Reservar vaga para esta reserva'));
    }
    return el('div', { class: 'card', id: 'balcao-garagem', dataset: { vagas: String(vs.length) }, style: { padding: '14px 16px', marginBottom: '12px' } },
      vs.map(v => {
        const selo = seloDaVaga(v);
        const linhaV = el('div', { dataset: { vaga: v.id, liberada: v.liberada_em ? 'sim' : 'nao' }, style: { padding: '4px 0' } },
          titulo(el('span', { class: 'badge ' + selo.classe }, selo.texto)),
          el('div', { class: 'usu-email', style: { marginBottom: '10px' } },
            `${v.setor ? v.setor + ' · ' : ''}${dataCurta(v.de, hoje())} → ${dataCurta(v.ate, hoje())}`));
        if (v.liberada_em) return linhaV;
        if (!dentro) {
          linhaV.appendChild(el('div', { class: 'usu-email' }, 'A placa é pedida no check-in da primeira pessoa.'));
          return linhaV;
        }
        const placa = el('input', { class: 'input', value: v.placa || '', placeholder: 'ABC1D23', maxlength: '8', autocomplete: 'off', dataset: { placaVaga: v.id }, style: { fontWeight: '700', letterSpacing: '1px' },
          oninput: ev => { ev.target.value = normalizarPlaca(ev.target.value); } });
        linhaV.appendChild(el('div', { style: { display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', alignItems: 'end' } },
          el('div', null, el('label', { class: 'label' }, 'Placa do carro'), placa),
          el('button', { type: 'button', class: 'btn btn-primary', dataset: { liberarVaga: v.id }, onclick: async () => {
            if (!normalizarPlaca(placa.value)) { aviso('Informe a placa do carro', 'erro'); return; }
            try { await auth.rpc('liberar_garagem', { p_pedido: v.id, p_placa: placa.value }); } catch (e) { aviso(e.message, 'erro'); return; }
            aviso(`Vaga ${v.vaga} liberada na portaria.`); recarregarFicha();
          } }, 'Liberar na portaria')));
        return linhaV;
      }));
  }

  // Uma pessoa, em DOIS BLOCOS — escolha do Hugo em 19/09 ("as duas
  // coisas, em blocos separados"):
  //
  //   1. o que libera a chave (documento e nascimento), que o banco cobra
  //   2. a ficha da FNRH, que o hóspede preencheu no link ou que a
  //      recepção preenche aqui
  //
  // Os dois blocos gravam pela MESMA função, `salvar_hospede` (0025), e a
  // diferença está no `p_editar_fnrh`: com ele falso, as onze colunas da
  // ficha são PRESERVADAS. Por isso o bloco 1 salva sozinho sem risco de
  // apagar o que o hóspede digitou no celular.
  function blocoPessoa(reserva, p) {
    const o = origemDaFicha(p);
    const falta = faltaParaAChave(p);
    const editando = balcao.editando === p.hospede_id;
    return el('div', { class: 'card', dataset: { fichaPessoa: p.hospede_id, origem: o.chave }, style: { padding: '14px 16px', marginBottom: '12px' } },
      el('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' } },
        el('span', { class: 'usu-nome', style: { fontSize: '16px' } }, p.nome),
        p.principal ? el('span', { class: 'badge badge-blue' }, 'titular') : null,
        p.menor ? el('span', { class: 'badge badge-gray' }, 'menor') : null,
        el('span', { class: 'badge ' + o.classe, dataset: { origemDe: p.hospede_id } }, o.rotulo),
        p.preenchida_em ? el('span', { class: 'usu-email' }, 'em ' + dataBR(String(p.preenchida_em).slice(0, 10))) : null,
        el('span', { style: { flex: '1' } }),
        podeEditar ? el('button', {
          type: 'button', class: 'btn btn-secondary btn-sm', dataset: { editarFicha: p.hospede_id },
          onclick: () => { balcao.editando = editando ? null : p.hospede_id; desenharQuadro(); },
        }, editando ? 'Cancelar' : 'Preencher') : null),

      // BLOCO 1 — o que trava a chave
      falta.length
        ? el('p', { class: 'alert alert-error', dataset: { falta: p.hospede_id }, style: { margin: '0 0 10px' } },
          'Falta ' + falta.join(' e ') + ' — sem isso o banco recusa o check-in.')
        : el('p', { class: 'usu-email', dataset: { falta: p.hospede_id }, style: { margin: '0 0 10px' } },
          `${p.tipo_documento || 'Doc'} ${formatarDocumento(p.documento, p.tipo_documento)} · nasc. ${dataBR(p.data_nascimento)}`),

      // BLOCO 2 — a ficha da FNRH
      el('div', { dataset: { fnrh: p.hospede_id }, style: { borderTop: '1px solid #f3f4f6', paddingTop: '10px' } },
        el('div', { class: 'usu-email' },
          `Ficha nacional de hóspede: ${fnrhPreenchidos(p, coletaRaca)} de ${totalFnrh(p, coletaRaca)} campos`
          + (o.chave === 'hospede' ? ' — o que está aí veio do link que o hóspede abriu' : ' — ninguém preencheu pelo link'))),

      // O FORMULÁRIO fica FORA dos dois blocos, e não dentro do segundo.
      // A primeira versão o enfiou dentro do bloco da FNRH, e a imagem
      // mostrou o estrago: documento, tipo e nascimento — que são do
      // bloco da CHAVE — apareciam sob o título "Ficha nacional de
      // hóspede". A tela contava uma história e o Hugo tinha pedido
      // outra. Aqui ele tem os dois títulos, na mesma ordem dos blocos.
      editando ? formularioFicha(reserva, p) : null);
  }

  function formularioFicha(reserva, p) {
    const f = fichaDoHospede(p);
    // 0041/0042 + regra permanente do Hugo (19/09/2026): "sempre utilize
    // nome e sobrenome na identificacao de hospedes em qualquer situacao".
    // O balcão é onde a recepcionista está com o documento do hóspede na
    // mão — é o melhor lugar do app para o corte do nome ficar certo, e
    // era o único que ainda mandava `p_sobrenome` vazio.
    const n = separarNome(p);
    const dados = {
      nome: n.nome, sobrenome: n.sobrenome, documento: p.documento || '', tipo_documento: p.tipo_documento || '',
      data_nascimento: p.data_nascimento || '', telefone: p.telefone || '', email: p.email || '',
    };
    const previaTexto = el('span', { dataset: { previa: p.hospede_id } });
    function previa() {
      const c = nomeCompleto(dados.nome, dados.sobrenome);
      previaTexto.textContent = c ? `Vai ser gravado como: ${c}` : 'Nome e sobrenome são obrigatórios.';
    }
    const campo = (ch, rot, tipo = 'text') => el('div', { class: 'form-row' },
      el('label', { class: 'label' }, rot),
      el('input', { class: 'input', type: tipo, id: 'fn-' + ch, value: f[ch] || '', oninput: ev => { f[ch] = ev.target.value; } }));
    // 0065: linhas que só aparecem com a resposta que as pede (gênero
    // "Outro" → descrição; deficiência "Sim" → tipo)
    const condicionais = {};
    const condicional = (ch, visivel, linha) => { linha.classList.toggle('hidden', !visivel); linha.dataset.condicional = ch; condicionais[ch] = linha; return linha; };
    const DEPENDE = { sexo: ['genero_descricao', 'OUTRO'], deficiencia: ['tipo_deficiencia', 'SIM'] };
    const escolha = (ch, rot, opcoes) => el('div', { class: 'form-row' },
      el('label', { class: 'label' }, rot),
      el('select', { class: 'input', id: 'fn-' + ch, onchange: ev => {
        f[ch] = ev.target.value;
        const d = DEPENDE[ch]; if (d && condicionais[d[0]]) condicionais[d[0]].classList.toggle('hidden', f[ch] !== d[1]);
      } },
        el('option', { value: '' }, '—'),
        opcoes.map(o => {
          const [v, r] = Array.isArray(o) ? o : [o, o];
          return el('option', { value: v, selected: String(f[ch]) === String(v) }, r);
        })));
    const titulo = (t, sub) => el('div', { style: { margin: '14px 0 8px' } },
      el('div', { style: { fontSize: '13px', fontWeight: '700', color: '#374151' } }, t),
      el('div', { class: 'usu-email' }, sub));
    previa();
    return el('div', { dataset: { formulario: p.hospede_id }, style: { borderTop: '1px solid #e5e7eb', marginTop: '10px' } },
      // O documento e o nascimento ficam AQUI, e não num modal à parte: é
      // a mesma conversa com o mesmo hóspede, e mandá-la para outra tela
      // é o que a barra de busca existe para evitar.
      titulo('Quem é', 'Nome e sobrenome, sempre — os dois vão separados para o banco.'),
      el('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' } },
        el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'fn-nome' }, 'Nome'),
          el('input', { class: 'input', id: 'fn-nome', value: dados.nome, autocomplete: 'given-name', oninput: ev => { dados.nome = ev.target.value; previa(); } })),
        el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'fn-sobrenome' }, 'Sobrenome'),
          el('input', { class: 'input', id: 'fn-sobrenome', value: dados.sobrenome, autocomplete: 'family-name', oninput: ev => { dados.sobrenome = ev.target.value; previa(); } }))),
      // A mesma prévia do modal de Hóspedes, e pelo mesmo motivo: ficha de
      // antes da 0041 volta com o nome completo no primeiro campo, e quem
      // digitar o sobrenome ao lado sem limpar o primeiro grava o nome
      // dobrado. Mostrar o resultado é honesto; adivinhar onde o nome
      // parte não é.
      el('div', { class: 'usu-email', style: { margin: '-4px 0 6px' } }, previaTexto),
      titulo('Para liberar a chave', 'Sem estes dois o banco recusa o check-in.'),
      el('div', { class: 'form-row' }, el('label', { class: 'label' }, 'Documento'),
        el('input', { class: 'input', id: 'fn-documento', value: dados.documento, oninput: ev => { dados.documento = ev.target.value; } })),
      el('div', { class: 'form-row' }, el('label', { class: 'label' }, 'Tipo do documento'),
        el('select', { class: 'input', id: 'fn-tipo_documento', onchange: ev => { dados.tipo_documento = ev.target.value; } },
          el('option', { value: '' }, '—'),
          // DEFEITO CORRIGIDO EM 19/09/2026: aqui estava
          // `['CPF', 'RG', 'PASSAPORTE']`, escrito a mão. 'RG' NÃO EXISTE
          // neste app — `TIPOS_DOCUMENTO` (0016) tem dois tipos, e
          // `validarHospede` recusa qualquer outro com "Tipo de documento
          // é CPF ou Passaporte". Quem escolhesse RG levaria o erro na
          // cara ao salvar, num campo que a própria tela ofereceu. A lista
          // agora vem da constante compartilhada, que é o único lugar onde
          // essa verdade deve morar.
          TIPOS_DOCUMENTO.map(([v, r]) => el('option', { value: v, selected: dados.tipo_documento === v }, r)))),
      el('div', { class: 'form-row' }, el('label', { class: 'label' }, 'Data de nascimento'),
        el('input', { class: 'input', type: 'date', id: 'fn-data_nascimento', value: dados.data_nascimento || '', oninput: ev => { dados.data_nascimento = ev.target.value; } })),
      titulo('Ficha nacional de hóspede',
        p.origem === 'hospede'
          ? 'O hóspede preencheu parte disto no link. O que estiver em branco é o que ele deixou.'
          : 'Ninguém preencheu pelo link — esta parte é do balcão.'),
      // 0062 (módulo FNRH): nome social, sexo, bairro, complemento e o
      // legalmente incapaz — os campos da ficha oficial que faltavam
      campo('nome_social', 'Nome social (só se a pessoa usar)'),
      escolha('sexo', 'Gênero', SEXOS),
      condicional('genero_descricao', f.sexo === 'OUTRO', el('div', { class: 'form-row' },
        el('label', { class: 'label' }, 'Descrição do gênero'),
        el('input', { class: 'input', id: 'fn-genero_descricao', maxlength: String(MAX_GENERO_DESCRICAO), value: f.genero_descricao || '', oninput: ev => { f.genero_descricao = ev.target.value; } }))),
      coletaRaca ? escolha('raca', 'Raça/cor (autodeclarada)', RACAS) : null,
      // 0065: deficiência — saúde, a mesma trava da raça
      coletaRaca ? escolha('deficiencia', 'Pessoa com deficiência', DEFICIENCIAS) : null,
      coletaRaca ? condicional('tipo_deficiencia', f.deficiencia === 'SIM', escolha('tipo_deficiencia', 'Tipo de deficiência', TIPOS_DEFICIENCIA)) : null,
      // 0070: país e município da lista do IBGE, endereço partido
      seletorPais(f, 'nacionalidade_pais', 'Nacionalidade', { id: 'fn-nacionalidade_pais', antes: textoAntigo(p).nacionalidade_pais }),
      campo('profissao', 'Profissão'),
      blocoResidencia(f, { id: c => 'fn-' + c, antigo: textoAntigo(p) }),
      campo('procedencia', 'De onde vem'),
      campo('destino', 'Para onde vai'),
      escolha('motivo_viagem', 'Motivo da viagem', MOTIVOS_VIAGEM),
      escolha('meio_transporte', 'Meio de transporte', MEIOS_TRANSPORTE),
      el('label', { class: 'label', style: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: '6px 0 12px' } },
        el('input', { type: 'checkbox', id: 'fn-legalmente_incapaz', checked: !!f.legalmente_incapaz, onchange: ev => { f.legalmente_incapaz = ev.target.checked; } }),
        'Pessoa legalmente incapaz (sob tutela ou curatela)'),
      el('button', {
        type: 'button', class: 'btn btn-primary', id: 'balcao-salvar',
        onclick: () => salvarFicha(reserva, p, dados, f),
      }, 'Salvar ficha'));
  }

  async function salvarFicha(reserva, p, dados, f) {
    // A validação é a COMPARTILHADA (`validarHospede`), e não uma cópia
    // daqui: é ela que carrega a regra permanente do nome e sobrenome, e é
    // ela que o modal de Hóspedes e o link do hóspede também chamam. Antes
    // desta linha o quadro do balcão ia direto ao banco, e um tipo de
    // documento inválido só aparecia como erro do Postgres.
    f = fichaParaSalvar(f);   // 0065: o par coerente (descrição só com Outro, tipo só com Sim)
    const erros = [...validarHospede(dados, hoje()), ...errosDaFicha(f)];
    if (erros.length) { aviso(erros.join(' · '), 'erro'); return; }
    try {
      await auth.rpc('salvar_hospede', {
        ...argumentosDoHospede({ id: p.hospede_id, reserva_id: reserva.id, dados, ficha: f, principal: p.principal, editarFnrh: true }),
        ...pc(),
      });
    } catch (e) { aviso(e.message, 'erro'); return; }
    aviso(`Ficha de ${p.nome} salva.`);
    balcao.editando = null;
    // A ficha é relida do BANCO, não remendada na memória: a `origem` e o
    // `completa` são derivados lá (0038), e recalculá-los aqui seria uma
    // segunda versão da mesma verdade — a primeira a ficar errada.
    await abrirFicha(reserva.id);
    // e a lista de resultados também, porque o farol dela mudou
    if (buscaPronta(balcao.texto)) {
      try {
        balcao.achados = await auth.rpc('buscar_no_balcao', { p_texto: balcao.texto, ...pc() });
        if (!reencontrar(balcao.achados, balcao.abertaId)) fecharFicha(); else desenharResultados();
      } catch (_) { /* a ficha já salvou; a lista se conserta na próxima tecla */ }
    }
    recarregar();   // o hóspede pode ter passado a poder fazer check-in
  }

  function desenharLista() {
    const alvo = cont.querySelector('#ci-lista'); if (!alvo) return;
    alvo.replaceChildren();
    const todas = agruparCheckin(linhas, dia);
    const lista = filtrarCheckin(todas, filtro, dia);
    if (!lista.length) {
      alvo.appendChild(el('p', { class: 'vazio', id: 'ci-vazio' }, todas.length ? (filtro.texto ? 'Nada com esse filtro.' : 'Nada nesta aba.') : 'Nenhuma reserva neste dia.'));
      return;
    }
    for (const r of lista) alvo.appendChild(cartao(r));
  }

  function cartao(r) {
    const f = FASES_CHECKIN[r.fase];
    const checkout = podeCheckout(r, { podeEditar });
    return el('div', { class: 'card', style: { padding: '12px 14px', marginBottom: '10px' }, dataset: { reserva: r.id, fase: r.fase, lotacao: r.lotacao } },
      el('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' } },
        el('span', { class: 'usu-nome', style: { fontSize: '16px' } }, 'Quarto ' + r.quarto),
        el('span', { class: 'badge ' + f.classe }, f.rotulo),
        el('span', { class: 'usu-email' }, `${dataCurta(r.check_in, hoje())} → ${dataCurta(r.check_out, hoje())} · ${noites(r.check_in, r.check_out)} noite(s)`),
        r.imobiliaria ? el('span', { class: 'usu-email' }, r.imobiliaria) : null,
        r.numero ? el('span', { class: 'usu-email' }, `nº ${r.numero}`) : null,
        el('span', { class: 'badge ' + (r.semGente ? 'badge-red' : r.chegaram === r.total ? 'badge-green' : 'badge-gray'), dataset: { lotacao: r.id } }, r.semGente ? 'ninguém cadastrado' : `${r.lotacao} dentro`),
        // 0021/0022: a faixa de chegada e a placa que o hóspede informou — só enquanto ainda não chegou
        (r.fase === 'chega' && (r.chegada_prevista || r.placa_veiculo)) ? el('span', { class: 'usu-email', dataset: { chegada: r.chegada_prevista || '' } },
          [r.chegada_prevista ? `chega ${rotuloFaixa(r.chegada_prevista).toLowerCase()}` : null, r.placa_veiculo ? `placa ${r.placa_veiculo}` : null].filter(Boolean).join(' · ')) : null,
        // 0097: a vaga da garagem da reserva, com o mesmo selo do cartão da ficha
        ...(editaGaragem ? vagasDe(r.id).map(v => { const s = seloDaVaga(v); return el('span', { class: 'badge ' + s.classe, dataset: { vagaDaReserva: v.id } }, s.texto); }) : []),
        el('span', { style: { flex: '1' } }),
        checkout ? el('button', { type: 'button', class: 'btn btn-primary btn-sm', dataset: { checkout: r.id }, onclick: () => fazerCheckout(r) }, icone('ic-checkin'), 'Check-out') : null,
        // 0093 (1A): check-out por engano (quarto errado) se desfaz no mesmo dia
        podeDesfazerCheckout(r, { podeEditar, hoje: hoje(), diaDe: t => hojeDe(contexto, Date.parse(t)) })
          ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', dataset: { desfazerCheckout: r.id }, title: 'Check-out marcado por engano', onclick: () => desfazerCheckout(r) }, 'Desfazer check-out') : null,
        // 0061: só no dia de hoje — atender é agora, não num dia consultado
        (podeEditar && r.aguardaAtendimento && dia === hoje())
          ? el('button', { type: 'button', class: 'btn btn-primary btn-sm', dataset: { atender: r.id }, onclick: () => atender(r) }, icone('ic-checkin'), 'Atender') : null),
      r.semGente
        ? el('p', { class: 'usu-email', style: { margin: '8px 0 0' } }, temHospedes ? 'Ninguém cadastrado ainda — cadastre em Hóspedes.' : 'Ninguém cadastrado ainda.')
        : el('div', { style: { marginTop: '8px' } }, r.gente.map(h => linha(r, h))));
  }

  function linha(r, h) {
    const a = acaoDoHospede(h, r, { podeEditar });
    const doc = h.documento ? `${h.tipo_documento || 'Doc'} ${formatarDocumento(h.documento, h.tipo_documento)}` : (h.documento_ok ? 'com documento' : 'sem documento');
    return el('div', { class: 'ficha-item', dataset: { hospede: h.id, fila: h.fila }, style: { display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', borderTop: '1px solid #f3f4f6' } },
      el('span', { style: { flex: '1' } },
        el('span', { class: 'usu-nome' }, h.nome),
        h.principal ? el('span', { class: 'badge badge-blue', style: { marginLeft: '6px' } }, 'titular') : null,
        h.menor ? el('span', { class: 'badge badge-gray', style: { marginLeft: '6px' } }, 'menor') : null,
        h.saida_em ? el('span', { class: 'badge badge-gray', style: { marginLeft: '6px' } }, `saiu ${horaCurta(h.saida_em)}`)
          : h.chegada_em ? el('span', { class: 'badge badge-green', style: { marginLeft: '6px' } }, `chegou ${horaCurta(h.chegada_em)}`)
          : h.fila === 'pendente' ? el('span', { class: 'badge badge-red', style: { marginLeft: '6px' } }, 'sem documento') : null,
        el('div', { class: 'usu-email' }, doc)),
      a.acao === 'checkin' ? el('button', { type: 'button', class: 'btn btn-verde btn-sm', dataset: { checkin: h.id }, onclick: () => fazerCheckin(r, h) }, 'Check-in') : null,
      a.acao === 'desfazer' ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', dataset: { desfazer: h.id }, title: 'Marcado por engano', onclick: () => desfazer(r, h) }, 'Desfazer') : null,
      !a.acao && a.motivo && podeEditar && r.situacao === 'confirmada' && !h.saida_em ? el('span', { class: 'usu-email', dataset: { motivo: h.id } }, a.motivo) : null);
  }

  // 0071 — o envio à FNRH, decisão do Hugo (29/09/2026): "a tela, na hora".
  // O banco já pôs o item na fila junto com o check-in/check-out; aqui só se
  // pede à Edge Function que envie. Não se espera por ela: a recepção segue,
  // e o que falhar fica pendente para o próximo envio ou o Reenviar da tela
  // FNRH. Só se avisa quando há erro.
  function enviarFnrh() {
    if (!fnrhLigado) return;
    auth.funcao('fnrh', { acao: 'enviar', ...(sistema ? { cliente_id: clienteSel } : {}) })
      .then(r => { const m = avisoDoDisparo(r); if (m && vivo) aviso(m, 'erro'); },
            e => { if (vivo) aviso(`Envio à FNRH: ${e.message} — fica pendente; veja em FNRH.`, 'erro'); });
  }

  // 0097 (6A, 1B): no 1º check-in de uma reserva com UMA vaga aguardando,
  // a placa vem no mesmo diálogo, e check-in + liberação são uma transação
  // só no banco (fazer_checkin com p_placa). Placa vazia: o check-in de
  // sempre, e a vaga espera no cartão Garagem da ficha.
  async function fazerCheckin(r, h) {
    const vaga = editaGaragem ? vagaDoPrimeiroCheckin(r, vagasDe(r.id)) : null;
    if (vaga) return modalCheckinComPlaca(r, h, vaga);
    try { await auth.rpc('fazer_checkin', { p_hospede_id: h.id, ...pc() }); } catch (e) { aviso(e.message, 'erro'); return; }
    aviso(`${h.nome} chegou · quarto ${r.quarto}.`); recarregar(); refazerFicha(r.id); enviarFnrh();
  }
  // 0097: a ficha aberta no balcão é da mesma reserva? Relê — o cartão
  // Garagem depende de quem já está dentro (o "Liberar na portaria").
  function refazerFicha(reservaId) {
    if (editaGaragem && balcao.abertaId === reservaId) abrirFicha(reservaId, { jaAtendida: true });
  }
  function modalCheckinComPlaca(r, h, vaga) {
    const placa = el('input', { class: 'input', id: 'ck-placa', value: normalizarPlaca(vaga.placa || r.placa_veiculo || ''), placeholder: 'ABC1D23', maxlength: '8', autocomplete: 'off',
      style: { fontWeight: '700', letterSpacing: '1px' }, oninput: () => { placa.value = normalizarPlaca(placa.value); botao.textContent = normalizarPlaca(placa.value) ? 'Check-in e liberar vaga' : 'Check-in (a vaga aguarda)'; } });
    const botao = el('button', { type: 'submit', class: 'btn btn-verde', id: 'ck-placa-sim', style: { flex: '1' } }, 'Check-in e liberar vaga');
    const erro = el('div', { id: 'modal-erro', class: 'alert alert-error hidden', role: 'alert' });
    const origem = vaga.placa ? 'Veio da reserva da vaga.' : r.placa_veiculo ? 'Veio do pré-check-in.' : 'O hóspede não informou placa.';
    const form = el('form', { id: 'form-checkin-placa', onsubmit: async ev => {
      ev.preventDefault();
      const p = normalizarPlaca(placa.value);
      try { await auth.rpc('fazer_checkin', { p_hospede_id: h.id, ...pc(), ...(p ? { p_placa: p } : {}) }); }
      catch (e) { erro.textContent = e.message; erro.classList.remove('hidden'); return; }
      fecharModal();
      aviso(p ? `${h.nome} chegou · quarto ${r.quarto} · vaga ${vaga.vaga} liberada.` : `${h.nome} chegou · quarto ${r.quarto}. A vaga ${vaga.vaga} aguarda a placa.`);
      recarregar(); refazerFicha(r.id); enviarFnrh();
    } },
      el('h2', { class: 'modal-title' }, `Check-in de ${h.nome} · quarto ${r.quarto}`),
      erro,
      el('div', { style: { fontSize: '13px', color: '#374151', marginBottom: '12px' } },
        'A reserva tem a vaga ', el('b', null, vaga.vaga), `${vaga.setor ? ' (' + vaga.setor + ')' : ''} até ${dataCurta(vaga.ate, hoje())}.`),
      el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'ck-placa' }, 'Placa do carro'), placa,
        el('div', { style: { fontSize: '11px', color: '#6b7280', marginTop: '4px' } }, `${origem} Vazia: o check-in acontece e a vaga fica aguardando.`)),
      el('div', { style: { display: 'flex', gap: '8px', marginTop: '16px' } },
        el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: fecharModal }, 'Cancelar'),
        botao));
    abrirModal(form);
    if (!normalizarPlaca(placa.value)) botao.textContent = 'Check-in (a vaga aguarda)';
  }
  async function desfazer(r, h) {
    const sim = await confirmar({ titulo: `Desfazer o check-in de ${h.nome}?`, texto: 'Só para chegada marcada por engano. A trilha guarda o desfazer.', sim: 'Desfazer', perigoso: true });
    if (!sim) return;
    try { await auth.rpc('desfazer_checkin', { p_hospede_id: h.id, ...pc() }); } catch (e) { aviso(e.message, 'erro'); return; }
    aviso('Check-in desfeito.'); recarregar();
  }
  async function fazerCheckout(r) {
    const dentro = r.gente.filter(h => h.chegada_em && !h.saida_em);
    const sim = await confirmar({ titulo: `Check-out do quarto ${r.quarto}?`, texto: `${dentro.length} pessoa(s) saem agora: ${dentro.map(h => h.nome).join(', ')}. A reserva fica concluída e o quarto, livre.`, sim: 'Check-out' });
    if (!sim) return;
    let n;
    try { n = await auth.rpc('fazer_checkout', { p_reserva_id: r.id, ...pc() }); } catch (e) { aviso(e.message, 'erro'); return; }
    aviso(`Quarto ${r.quarto} liberado · ${n} pessoa(s).`); recarregar(); enviarFnrh();
  }

  async function desfazerCheckout(r) {
    const sim = await confirmar({ titulo: `Desfazer o check-out do quarto ${r.quarto}?`,
      texto: 'Só para check-out marcado por engano, no mesmo dia. A reserva volta a confirmada, as pessoas voltam para dentro e a vaga da garagem volta. A trilha guarda o desfazer.',
      sim: 'Desfazer', perigoso: true });
    if (!sim) return;
    let n;
    try { n = await auth.rpc('desfazer_checkout', { p_reserva_id: r.id, ...pc() }); } catch (e) { aviso(e.message, 'erro'); return; }
    aviso(`Check-out do quarto ${r.quarto} desfeito · ${n} pessoa(s) de volta.`); recarregar();
  }

  recarregar();
  return () => { vivo = false; };
}
